/**
 * Overage cost: what an org pays for usage beyond its included allowance.
 *
 * overage units = max(0, estimatedUsage − includedAllowance)
 * cost          = overage units priced through the meter's rate tiers
 *
 * Callers pass the estimated usage RANGE (see `selectTotalOverageCost`) so the
 * result brackets the monthly spend with a low/high bound derived from the low/
 * high usage estimate.
 */

import type { Meter } from './types';

/**
 * A cumulative pricing tier, expressed in overage units. `upTo` is the
 * inclusive upper bound of overage covered by this tier; the final tier uses
 * Infinity. `pricePerUnit` is the per-overage-unit price in USD.
 */
interface RateTier {
	upTo: number;
	pricePerUnit: number;
}

/**
 * Per-meter overage rates:
 *   Rovo credits  — $0.01/credit ($10 per 1,000)
 *   CS resolutions — $1.00/resolution
 *   Automations   — $0.50 per 1,000 steps ($0.0005/step)
 *   Assets objects — $0.02/object for the first 100K over allowance,
 *                    $0.01/object beyond 100K
 */
const RATE_TIERS: Record<Meter, RateTier[]> = {
	rovoCredits: [{ upTo: Infinity, pricePerUnit: 0.01 }],
	csResolutions: [{ upTo: Infinity, pricePerUnit: 1.0 }],
	automations: [{ upTo: Infinity, pricePerUnit: 0.5 / 1_000 }],
	assetsObjects: [
		{ upTo: 100_000, pricePerUnit: 0.02 },
		{ upTo: Infinity, pricePerUnit: 0.01 },
	],
};

/**
 * How many raw units each meter's overage price is quoted against.
 * Automations are quoted "per 1,000 steps", so their block size is 1,000;
 * the rest are priced per single unit. Purely numeric — the customer-facing
 * unit noun ("steps", "resolution", …) lives in the content layer.
 */
const RATE_QUOTE_QUANTITY: Record<Meter, number> = {
	rovoCredits: 1,
	csResolutions: 1,
	automations: 1_000,
	assetsObjects: 1,
};

/** One tier of a meter's overage rate as a numeric quote. */
export interface OverageRateTier {
	/** Price in USD for one quoting block of units (e.g. $0.50 per 1,000 steps). */
	price: number;
}

/**
 * The meter's overage rate as numeric price tiers, each rescaled to the
 * customer-facing quoting block (e.g. automations become $0.50 per 1,000
 * steps). Single-element for flat meters; multi-element for tiered meters
 * like assets objects. Display copy (nouns, tier phrasing) is applied by the
 * content layer.
 */
export function getOverageRateTiers(meter: Meter): OverageRateTier[] {
	const quantity = RATE_QUOTE_QUANTITY[meter];
	return RATE_TIERS[meter].map((tier) => ({ price: tier.pricePerUnit * quantity }));
}

export interface Bound {
	low: number;
	high: number;
}

export interface OverageCostArgs {
	meter: Meter;
	/** Low/high bounds of the estimated monthly usage range for the meter. */
	estimatedUsage: Bound;
	/** Included allowance summed across selected products for the meter. */
	includedAllowance: number;
}

export interface OverageCostResult {
	/** Usage units beyond the included allowance (never negative). */
	overageUnits: Bound;
	/** Monthly overage cost in USD (never negative). */
	cost: Bound;
}

/** Price a single usage figure against the meter's rate tiers. */
function priceUsage(meter: Meter, estimatedUsage: number, includedAllowance: number) {
	const overageUnits = Math.max(0, estimatedUsage - includedAllowance);
	if (overageUnits === 0) {
		return { overageUnits: 0, cost: 0 };
	}

	let cost = 0;
	let lowerBound = 0;
	for (const tier of RATE_TIERS[meter]) {
		const unitsInTier = Math.min(overageUnits, tier.upTo) - lowerBound;
		if (unitsInTier <= 0) {
			break;
		}
		cost += unitsInTier * tier.pricePerUnit;
		lowerBound = tier.upTo;
	}

	return { overageUnits, cost };
}

export function calculateOverageCost(args: OverageCostArgs): OverageCostResult {
	const { meter, estimatedUsage, includedAllowance } = args;

	const low = priceUsage(meter, estimatedUsage.low, includedAllowance);
	const high = priceUsage(meter, estimatedUsage.high, includedAllowance);

	return {
		overageUnits: { low: low.overageUnits, high: high.overageUnits },
		cost: { low: low.cost, high: high.cost },
	};
}
